import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';

import CourseMultiSelect from '@/components/CourseMultiSelect/CourseMultiSelect';
import { FormErrorMessage } from '@/components/FormErrorMessage';
import { showErrorToast, showSuccessToast } from '@/components/ToastNotification';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { updateUser } from '@/hooks/mutations/userMutations';
import { useIsBreakpoint } from '@/hooks/use-is-breakpoint';
import type { Course, User, UserWithCourses } from '@/lib/types';
import { uniqueByCourseId } from '@/lib/utils';
import { userDefinedRoles } from '@/Shared/Data/StaticData';

interface UserModalProps {
	triggerModal(): void;
	modalActive: boolean;
	modalData: UserWithCourses;
	deptChairList: User[];
}

interface FormValues {
	firstName: string;
	lastName: string;
	deptChairId: string;
	role: string;
}

const UserModal = ({ modalActive, triggerModal, modalData, deptChairList }: UserModalProps) => {
	const {
		register,
		handleSubmit,
		control,
		formState: { errors },
	} = useForm<FormValues>();
	const isMutatingUser = useIsMutating({
		mutationKey: ['user-info-update'],
	});
	const {
		dept_chair: deptChair,
		user_first: firstName,
		user_last: lastName,
		user_id: userId,
		user_role: userRole,
		courses_users: initialUserCourses,
	} = modalData;
	const [userCourses, setUserCourses] = useState<Pick<Course, 'course_id' | 'course_name'>[]>([]);
	const isMobile = useIsBreakpoint('max', 768);

	useEffect(() => {
		const abortController = new AbortController();
		const transformedCourses = uniqueByCourseId(
			initialUserCourses?.map((cu) => ({
				course_id: cu.course_id ?? '',
				course_name: cu.course?.course_name ?? '',
			})) ?? []
		);
		setUserCourses(transformedCourses);
		return () => {
			abortController.abort();
		};
	}, [initialUserCourses]);

	const createDeptList = (): { label: string; value: string; id: string }[] => {
		const formattedList = deptChairList
			.filter((c) => c.user_id !== userId)
			.map((c) => ({
				label: `${c.user_first ?? ''} ${c.user_last ?? ''}`,
				value: `${c.user_first ?? ''} ${c.user_last ?? ''}`,
				id: c.user_firebase_id,
			}));

		return formattedList;
	};

	const queryClient = useQueryClient();
	const schoolId = import.meta.env.VITE_SCHOOL_ID;
	const { mutate: userMutate, status } = useMutation({
		mutationKey: ['user-info-update'],
		mutationFn: updateUser,
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['get-users', schoolId],
			});
			queryClient.invalidateQueries({
				queryKey: ['get-dept-chairs', schoolId],
			});
			showSuccessToast('User Updated');
		},

		onError: () => {
			showErrorToast('Oh no, something went wrong... Please try again.');
		},
	});

	const handleModalClose = () => {
		if (status !== 'pending') {
			triggerModal();
		}
	};

	const onSubmit: SubmitHandler<FormValues> = async (formData) => {
		const { firstName, lastName, deptChairId, role } = formData;
		const currentUserCourses = modalData.courses_users ?? [];

		userMutate({
			userCourses,
			currentUserCourses,
			userId,
			firstName,
			lastName,
			role,
			deptChair: deptChairId ?? '',
		});
	};

	const handleOpenChange = (open: boolean) => {
		if (!open) handleModalClose();
	};

	const title = `Editing: ${firstName} ${lastName}`;

	const formContent = (
		<form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
			<Controller
				control={control}
				name="role"
				defaultValue={userRole ?? ''}
				render={({ field }) => (
					<Select value={field.value} onValueChange={field.onChange}>
						<SelectTrigger className="w-full">
							<SelectValue placeholder="Select Role" />
						</SelectTrigger>
						<SelectContent>
							{userDefinedRoles.map((role) => (
								<SelectItem key={role.value} value={role.value}>
									{role.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				)}
			/>
			<Controller
				control={control}
				name="deptChairId"
				defaultValue={
					deptChairList.find((c) => c.user_id === deptChair?.user_id)?.user_firebase_id ?? ''
				}
				render={({ field }) => (
					<Select
						value={field.value}
						onValueChange={(val) => field.onChange(val === '__none__' ? '' : val)}
					>
						<SelectTrigger className="w-full">
							<SelectValue placeholder="Choose Dept. Chair" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="__none__">None</SelectItem>
							{createDeptList().map((chair) => (
								<SelectItem key={chair.id} value={chair.id}>
									{chair.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				)}
			/>
			<Input
				type="text"
				defaultValue={firstName ?? ''}
				placeholder="First Name"
				{...register('firstName', {
					required: 'First name is required',
				})}
			/>
			<FormErrorMessage
				className="-mt-3"
				isShowing={!!errors?.firstName}
				message={errors.firstName?.message}
			/>
			<Input
				type="text"
				defaultValue={lastName ?? ''}
				placeholder="Last Name"
				{...register('lastName', {
					required: 'Last name is required',
				})}
			/>
			<FormErrorMessage
				className="-mt-3"
				isShowing={!!errors?.lastName}
				message={errors.lastName?.message}
			/>
			<div>
				<p className="text-sm font-medium pb-2">Assigned Courses</p>
				<CourseMultiSelect selectedCourses={userCourses} onChange={setUserCourses} />
				<div className="pb-4" />
			</div>
		</form>
	);

	const saveButton = (
		<Button
			isLoading={!!isMutatingUser}
			disabled={!!isMutatingUser}
			onClick={handleSubmit(onSubmit)}
		>
			Save
		</Button>
	);

	if (isMobile) {
		return (
			<Drawer open={modalActive} onOpenChange={handleOpenChange}>
				<DrawerContent className="max-h-[85vh]">
					<DrawerHeader>
						<DrawerTitle>{title}</DrawerTitle>
						<DrawerDescription>Update user details and course assignments.</DrawerDescription>
					</DrawerHeader>
					<div className="px-4 pb-2 flex-1 overflow-y-auto">{formContent}</div>
					<DrawerFooter>{saveButton}</DrawerFooter>
				</DrawerContent>
			</Drawer>
		);
	}

	return (
		<Dialog open={modalActive} onOpenChange={handleOpenChange}>
			<DialogContent className="max-w-[700px]">
				<DialogHeader>
					<DialogTitle className="pb-2">{title}</DialogTitle>
					<DialogDescription className="text-base">
						Update user details and course assignments.
					</DialogDescription>
				</DialogHeader>
				<div className="max-h-[70vh] overflow-y-auto">{formContent}</div>
				<div className="flex justify-end border-t pt-4">{saveButton}</div>
			</DialogContent>
		</Dialog>
	);
};

export default UserModal;
